import React, { useCallback, useEffect, useState } from 'react';
import { Button, Input, message, Modal, Table } from '@maxtropy/components';
import {
  apiV2WaterMapDataFlowmeterAddPost,
  apiV2WaterMapDataFlowmeterListPost,
  V2WaterMapDataFlowmeterListPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import styles from './index.module.scss';

export type MapCoordinate = { lng: number; lat: number };
type MapPointItem = Exclude<V2WaterMapDataFlowmeterListPostResponse['list'], undefined>[number];
type MapPointRow = { id: string; lng?: number; lat?: number; deviceId?: number; deviceName?: string };

interface MapPointModalProps {
  open: boolean;
  coordinate?: MapCoordinate;
  onClose: () => void;
}

export const formatCoordinate = ({ lng, lat }: MapCoordinate) => `经度：${lng.toFixed(6)}，纬度：${lat.toFixed(6)}`;

const MapPointModal: React.FC<MapPointModalProps> = ({ open, coordinate, onClose }) => {
  const [deviceId, setDeviceId] = useState('');
  const [mapPointRows, setMapPointRows] = useState<MapPointRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setDeviceId('');
  }, [coordinate, open]);

  const loadMapPoints = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiV2WaterMapDataFlowmeterListPost({});
      setMapPointRows(
        (response.list ?? []).map((item: MapPointItem, index) => ({
          id: `${item.flowmeterId ?? 'flowmeter'}-${index}`,
          lng: item.longitude,
          lat: item.latitude,
          deviceId: item.flowmeterId,
          deviceName: item.flowmeterName,
        }))
      );
    } catch (error) {
      message.error('流量计点位加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadMapPoints();
  }, [loadMapPoints, open]);

  const handleAddMapPoint = useCallback(async () => {
    const normalizedDeviceId = deviceId.trim();
    if (!coordinate || !normalizedDeviceId) return;

    const flowmeterId = Number(normalizedDeviceId);
    if (!Number.isInteger(flowmeterId) || flowmeterId <= 0) {
      message.error('请输入有效的设备 ID');
      return;
    }

    setSaving(true);
    try {
      await apiV2WaterMapDataFlowmeterAddPost({
        flowmeterIds: [
          {
            longitude: coordinate.lng,
            latitude: coordinate.lat,
            flowmeterId,
          },
        ],
      });
      message.success('流量计点位添加成功');
      setDeviceId('');
      await loadMapPoints();
    } catch (error) {
      message.error('流量计点位添加失败');
    } finally {
      setSaving(false);
    }
  }, [coordinate, deviceId, loadMapPoints]);

  return (
    <Modal
      title="添加流量计点位"
      open={open}
      width={760}
      centered
      footer={null}
      className={styles.pointModal}
      onCancel={onClose}
      getContainer={() => document.getElementById('full_screen_box') ?? document.body}
    >
      <div className={styles.coordinate}>{coordinate && <span>{formatCoordinate(coordinate)}</span>}</div>
      <div className={styles.pointForm}>
        <Input
          value={deviceId}
          inputMode="numeric"
          placeholder="请输入设备 ID"
          onChange={event => setDeviceId(event.target.value)}
          onPressEnter={handleAddMapPoint}
        />
        <Button type="primary" loading={saving} disabled={!coordinate || !deviceId.trim()} onClick={handleAddMapPoint}>
          添加
        </Button>
      </div>
      <Table<MapPointRow>
        rowKey="id"
        size="small"
        pagination={false}
        loading={loading}
        dataSource={mapPointRows}
        locale={{ emptyText: '暂无已添加点位' }}
        columns={[
          { title: '序号', width: 70, render: (_value, _record, index) => index + 1 },
          {
            title: '经度',
            dataIndex: 'lng',
            render: value => (Number.isFinite(value) ? Number(value).toFixed(6) : '--'),
          },
          {
            title: '纬度',
            dataIndex: 'lat',
            render: value => (Number.isFinite(value) ? Number(value).toFixed(6) : '--'),
          },
          { title: '设备 ID', dataIndex: 'deviceId', render: (value, record) => value ?? record.deviceName ?? '--' },
          {
            title: '操作',
            width: 80,
            render: (_value, record) => (
              <Button
                danger
                type="link"
                onClick={() => setMapPointRows(rows => rows.filter(item => item.id !== record.id))}
              >
                删除
              </Button>
            ),
          },
        ]}
      />
    </Modal>
  );
};

export default MapPointModal;
